import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { hasStoredSession } from '@/lib/logto';

/**
 * Auth slice — holds the QLASH user record fetched from `GET /users/me`
 * plus a single source-of-truth `bridgeAuthenticated` flag for the Telegram
 * bridge flow. AppShell, Profile, and Settings all read from here so they
 * stay in sync after sign-in / sign-out.
 *
 * The access token itself is owned by `lib/logto.ts` (manual storage) or by
 * the Logto SDK (standalone web flow) — this slice does not track it.
 */

export interface AuthUser {
  id: number | string;
  username?: string;
  /**
   * Il nickname QLASH, e SOLO quello: `nickname` di `GET /v1/me` con `requiresNickname === false`
   * (`lib/qgammon/displayName.ts`). ⛔ NON e' `username`: quello ripiega sul `name` di Limulo quando
   * `requiresNickname` manca (`lib/limulo/identity.ts`), cioe' puo' essere un valore in forma che non e'
   * il nickname. Lo legge solo `useLogtoAuth` per darlo a QGammon (`X-QGammon-Display-Name`).
   */
  nicknameQlash?: string;
  /**
   * `true` = questa persona **non ha ancora scelto** un nome pubblico (ADR-0125).
   * `undefined` = **non lo sappiamo** — o l'identita' non e' ancora arrivata, o la sorgente
   * non espone il campo (il monolite non lo fa).
   * `null` = **non lo sappiamo, detto dal server** (kb#145, 21/09): team, placeholder e
   * identita' non risolvibile. Da leggere come `undefined`, mai come `false`.
   * ⛔ I tre casi non si collassano: `undefined` e `null` non sono `false`, e i confronti
   * restano `=== true` / `=== false` — un `!== undefined` lascerebbe passare il `null`. Chi pubblica un nome
   * pubblico deve pretendere un `false` esplicito — v. `hooks/useProfileSync.ts`.
   */
  requiresNickname?: boolean | null;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  email?: string;
  /**
   * QLASH admin flag from the Laravel `/users/me` payload. Drives the
   * DEV build access gate (`VITE_ADMIN_GATE_ENABLED=true`) alongside
   * `beta_tester`. Defaults to `false` when the backend omits the field so
   * non-admins are never accidentally elevated.
   */
  is_admin: boolean;
  /**
   * QLASH beta-tester flag from the Laravel `/users/me` payload. Grants
   * staging access (same as `is_admin`) in the DEV build, and is meant to
   * gate future beta-only features in PROD. Defaults to `false`.
   */
  beta_tester: boolean;
}

interface AuthState {
  user: AuthUser | null;
  /** Mirror of `hasStoredSession()` — drives the AppShell auth gate. True when
   * a fresh access token OR a redeemable refresh_token exists (persisted
   * session), so a returning user isn't bounced to Login on every reboot. */
  bridgeAuthenticated: boolean;
  /** Specchio di `useLogto().isAuthenticated` — la sessione dell'SDK Logto (il ripiego web, fuori da
   * Telegram). La scrive SOLO `useLogtoAuth`, in un layout effect. Esiste perche' chi non e' un
   * componente (la guardia di rete dell'ospite) deve leggere la sessione INTERA, non meta'. */
  sdkAuthenticated: boolean;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  bridgeAuthenticated: hasStoredSession(),
  sdkAuthenticated: false,
  status: 'idle',
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.status = 'idle';
      state.error = null;
    },
    /**
     * La scelta del nickname e' andata a buon fine ⇒ lo store smette di dire che manca.
     *
     * 🔑 Serve perche' `auth.user` **non viene da una query RTK**: su una build Limulo lo
     * popola il boot (`lib/limulo/identity.ts`), quindi invalidare il tag `LimuloMe` non lo
     * tocca. Senza questa riga la scrittura riesce e **sullo schermo non cambia niente**:
     * il richiamo resta, e `useProfileSync` continua a non pubblicare — cioe' il guasto che
     * la schermata esiste per chiudere sopravvive alla propria correzione.
     *
     * ⚠️ `requiresNickname` va a `false` **esplicito**, non cancellato: `undefined` significa
     * «non lo sappiamo» ed e' quello che la guardia di `useProfileSync` pretende di NON
     * vedere prima di pubblicare (v. il blocco in testa a quell'hook).
     */
    setNickname(state, action: PayloadAction<string>) {
      if (!state.user) return;
      state.user.username = action.payload;
      // La scelta appena salvata e' il nickname (`PUT /v1/me/nickname`): QGammon lo riceve senza aspettare un `/v1/me` nuovo.
      state.user.nicknameQlash = action.payload;
      state.user.requiresNickname = false;
    },
    setBridgeAuthenticated(state, action: PayloadAction<boolean>) {
      state.bridgeAuthenticated = action.payload;
    },
    setSdkAuthenticated(state, action: PayloadAction<boolean>) {
      state.sdkAuthenticated = action.payload;
    },
    setStatus(state, action: PayloadAction<AuthState['status']>) {
      state.status = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.status = action.payload ? 'error' : 'idle';
      state.error = action.payload;
    },
    logout(state) {
      state.user = null;
      state.bridgeAuthenticated = false;
      state.status = 'idle';
      state.error = null;
    },
  },
});

export const {
  setUser,
  setNickname,
  setBridgeAuthenticated,
  setSdkAuthenticated,
  setStatus,
  setError,
  logout,
} = authSlice.actions;

export default authSlice.reducer;
